// src/components/admin/GestionReferencias.js
import React, { useState, useEffect } from 'react';
import { Table, Button, Form, Modal, Alert, Spinner } from 'react-bootstrap';
import DatosMaestrosService from '../../services/datosMaestrosService';
import { usuarioService } from '../../services/usuarioService';
import MenuAccionesFila from './MenuAccionesFila';
import PanelFiltros, { CampoFiltro } from '../PanelFiltros';
import '../../style/components/GestionDatosMaestros.css';
import { coincideBusqueda } from '../../utils/textoComparable';

const GestionReferencias = () => {
    const [referencias, setReferencias] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sincronizando, setSincronizando] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState('create');
    const [selectedReferencia, setSelectedReferencia] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        descripcion: '',
        color: '#3B82F6',
        idUsuario: ''
    });
    const [alert, setAlert] = useState({ show: false, message: '', variant: '' });
    const [filterNombre, setFilterNombre] = useState('');
    const [seleccionadas, setSeleccionadas] = useState([]);
    const [showFusionModal, setShowFusionModal] = useState(false);
    const [idDestinoFusion, setIdDestinoFusion] = useState('');
    const [fusionando, setFusionando] = useState(false);

    useEffect(() => {
        cargarReferencias();
        cargarUsuarios();
    }, []);

    const cargarReferencias = async () => {
        setLoading(true);
        try {
            const response = await DatosMaestrosService.getReferencias();
            setReferencias(response.data);
        } catch {
            mostrarAlerta('Error al cargar referencias', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const cargarUsuarios = async () => {
        try {
            const response = await usuarioService.getAll();
            const validados = (response.data || []).filter(u => u.validado);
            setUsuarios(validados);
        } catch {
            // silencioso — el dropdown simplemente queda vacío
        }
    };

    const mostrarAlerta = (message, variant) => {
        setAlert({ show: true, message, variant });
        setTimeout(() => setAlert({ show: false, message: '', variant: '' }), 4000);
    };

    const handleOpenModal = (mode, referencia = null) => {
        setModalMode(mode);
        if (mode === 'edit' && referencia) {
            setSelectedReferencia(referencia);
            setFormData({
                nombre: referencia.nombre,
                descripcion: referencia.descripcion || '',
                color: referencia.color || '#3B82F6',
                idUsuario: referencia.idUsuario ?? ''
            });
        } else {
            setSelectedReferencia(null);
            setFormData({ nombre: '', descripcion: '', color: '#3B82F6', idUsuario: '' });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedReferencia(null);
        setFormData({ nombre: '', descripcion: '', color: '#3B82F6', idUsuario: '' });
    };

    const handleUsuarioChange = (e) => {
        const idUsuario = e.target.value;
        const usuario = usuarios.find(u => String(u.id) === idUsuario);
        setFormData(prev => ({
            ...prev,
            idUsuario,
            nombre: usuario ? usuario.nombre : prev.nombre
        }));
    };

    const buildPayload = () => ({
        nombre: formData.nombre,
        descripcion: formData.descripcion || null,
        color: formData.color,
        idUsuario: formData.idUsuario !== '' ? Number(formData.idUsuario) : null
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            if (modalMode === 'create') {
                await DatosMaestrosService.createReferencia(buildPayload());
                mostrarAlerta('Referencia creada exitosamente', 'success');
            } else {
                await DatosMaestrosService.updateReferencia(selectedReferencia.id, buildPayload());
                mostrarAlerta('Referencia actualizada exitosamente', 'success');
            }
            handleCloseModal();
            cargarReferencias();
        } catch {
            mostrarAlerta('Error al guardar la referencia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Está seguro de eliminar esta referencia?')) return;
        setLoading(true);
        try {
            await DatosMaestrosService.deleteReferencia(id);
            mostrarAlerta('Referencia eliminada exitosamente', 'success');
            cargarReferencias();
        } catch (error) {
            // El backend devuelve 409 con el motivo cuando la referencia es de sistema
            // ("OTRO CLIENTE"); ese mensaje es apto para mostrarlo tal cual.
            mostrarAlerta(error?.response?.data?.message || 'Error al eliminar la referencia', 'danger');
        } finally {
            setLoading(false);
        }
    };

    const handleSincronizar = async () => {
        setSincronizando(true);
        try {
            const response = await DatosMaestrosService.sincronizarReferenciasDesdeUsuarios();
            const creadas = response.data?.length ?? 0;
            mostrarAlerta(
                creadas > 0
                    ? `Se crearon ${creadas} referencia(s) desde usuarios del sistema`
                    : 'Todos los usuarios ya tienen su referencia asignada',
                'success'
            );
            cargarReferencias();
        } catch {
            mostrarAlerta('Error al sincronizar referencias', 'danger');
        } finally {
            setSincronizando(false);
        }
    };

    const enUso = (ref) => (ref.cantidadClientes ?? 0) + (ref.cantidadTurnos ?? 0) > 0;

    // En uso no se puede eliminar (la base lo impide): se ordena fusionándola con la que
    // corresponde. El menú lo dice en vez de fallar al tocarlo.
    const motivoNoEliminable = (ref) => {
        if (enUso(ref)) {
            return `La usan ${ref.cantidadClientes ?? 0} cliente(s) y ${ref.cantidadTurnos ?? 0} turno(s): fusionala con otra para sacarla del catálogo`;
        }
        return ref.esSistema ? 'Referencia del sistema: no se puede eliminar' : null;
    };

    const referenciasFiltradas = referencias.filter(ref =>
        coincideBusqueda(ref.nombre, filterNombre)
    );

    const toggleSeleccionada = (id) => {
        setSeleccionadas(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const handleAbrirFusion = () => {
        setIdDestinoFusion(String(seleccionadas[0]));
        setShowFusionModal(true);
    };

    const handleCerrarFusion = () => {
        setShowFusionModal(false);
        setIdDestinoFusion('');
    };

    const handleConfirmarFusion = async () => {
        if (!idDestinoFusion) return;
        setFusionando(true);
        try {
            const idDestino = Number(idDestinoFusion);
            const idsOrigen = seleccionadas.filter(id => id !== idDestino);
            const response = await DatosMaestrosService.fusionarReferencias(idsOrigen, idDestino);
            const cantidad = response.data?.clientesReasignados ?? 0;
            mostrarAlerta(
                `Referencias fusionadas. Se reasignaron ${cantidad} cliente(s) a la referencia elegida.`,
                'success'
            );
            setSeleccionadas([]);
            handleCerrarFusion();
            cargarReferencias();
        } catch (error) {
            // 409 cuando alguna de las referencias origen es de sistema ("OTRO CLIENTE"):
            // fusionarla la borraría del catálogo y dejaría clientes sin referencia.
            mostrarAlerta(error?.response?.data?.message || 'Error al fusionar las referencias', 'danger');
        } finally {
            setFusionando(false);
        }
    };

    const seleccionadasInfo = referencias.filter(ref => seleccionadas.includes(ref.id));

    return (
        <div className="gestion-datos-maestros">
            {alert.show && (
                <Alert variant={alert.variant} dismissible onClose={() => setAlert({ show: false, message: '', variant: '' })}>
                    {alert.message}
                </Alert>
            )}

            <div className="d-flex justify-content-end mb-3">
                <div className="d-flex flex-wrap justify-content-end gap-2">
                    {seleccionadas.length >= 2 && (
                        <Button variant="warning" onClick={handleAbrirFusion}>
                            <i className="bi bi-arrows-collapse me-2"></i>
                            Fusionar seleccionadas ({seleccionadas.length})
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        onClick={handleSincronizar}
                        disabled={sincronizando}
                        title="Crea automáticamente una referencia por cada usuario validado del sistema que aún no tenga una"
                    >
                        {sincronizando
                            ? <><Spinner animation="border" size="sm" className="me-1" />Sincronizando...</>
                            : <><i className="bi bi-arrow-repeat me-2"></i>Sincronizar desde usuarios</>
                        }
                    </Button>
                    <Button variant="primary" onClick={() => handleOpenModal('create')}>
                        <i className="bi bi-plus-circle me-2"></i>
                        Nueva Referencia
                    </Button>
                </div>
            </div>

            <PanelFiltros
                hayFiltros={filterNombre.trim() !== ''}
                onLimpiar={() => setFilterNombre('')}
                resumen={`Mostrando ${referenciasFiltradas.length} de ${referencias.length} referencias`}
            >
                <CampoFiltro etiqueta="Buscar" icono="bi-search" htmlFor="filtro-referencia-nombre" ancho>
                    <Form.Control
                        id="filtro-referencia-nombre"
                        className="panel-filtros-input"
                        type="text"
                        placeholder="Nombre de la referencia…"
                        value={filterNombre}
                        onChange={(e) => setFilterNombre(e.target.value)}
                    />
                </CampoFiltro>
            </PanelFiltros>

            {loading && referencias.length === 0 ? (
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            ) : (
                <div className="table-responsive">
                    <Table hover className="datos-maestros-table">
                        <thead>
                        <tr>
                            <th style={{ width: 40 }}></th>
                            <th>Nombre</th>
                            <th>Usuario asignado</th>
                            <th>Descripción</th>
                            <th>Color</th>
                            <th className="text-center" title="Clientes que tienen esta referencia">Clientes</th>
                            <th className="text-center" title="Turnos del turnero con esta referencia">Turnos</th>
                            <th className="text-end col-acciones"><span className="visually-hidden">Acciones</span></th>
                        </tr>
                        </thead>
                        <tbody>
                        {referenciasFiltradas.length === 0 ? (
                            <tr>
                                <td colSpan="8" className="text-center text-muted py-4">
                                    {referencias.length === 0
                                        ? "No hay referencias registradas"
                                        : "No se encontraron referencias que coincidan con la búsqueda"}
                                </td>
                            </tr>
                        ) : (
                            referenciasFiltradas.map((referencia) => (
                                <tr key={referencia.id}>
                                    <td>
                                        <Form.Check
                                            type="checkbox"
                                            checked={seleccionadas.includes(referencia.id)}
                                            onChange={() => toggleSeleccionada(referencia.id)}
                                        />
                                    </td>
                                    <td className="fw-semibold">{referencia.nombre}</td>
                                    <td>
                                        {referencia.nombreUsuario
                                            ? <><i className="bi bi-person-fill me-1 text-muted"></i>{referencia.nombreUsuario}</>
                                            : <span className="text-muted">—</span>
                                        }
                                    </td>
                                    <td>{referencia.descripcion || '—'}</td>
                                    <td>
                                        <div className="d-flex align-items-center gap-2">
                                            <div
                                                style={{
                                                    width: '28px',
                                                    height: '28px',
                                                    backgroundColor: referencia.color,
                                                    borderRadius: '6px',
                                                    border: '2px solid #e2e8f0'
                                                }}
                                            />
                                            <span className="text-muted small">{referencia.color}</span>
                                        </div>
                                    </td>
                                    <td className="text-center">{referencia.cantidadClientes ?? 0}</td>
                                    <td className="text-center">{referencia.cantidadTurnos ?? 0}</td>
                                    <td className="text-end">
                                        <MenuAccionesFila acciones={[
                                            { etiqueta: 'Editar', icono: 'bi-pencil', onClick: () => handleOpenModal('edit', referencia) },
                                            {
                                                etiqueta: 'Eliminar',
                                                icono: 'bi-trash',
                                                peligro: true,
                                                deshabilitado: Boolean(motivoNoEliminable(referencia)),
                                                motivo: motivoNoEliminable(referencia),
                                                onClick: () => handleDelete(referencia.id),
                                            },
                                        ]} />
                                    </td>
                                </tr>
                            ))
                        )}
                        </tbody>
                    </Table>
                </div>
            )}

            <Modal show={showModal} onHide={handleCloseModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>
                        {modalMode === 'create' ? 'Nueva Referencia' : 'Editar Referencia'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Usuario del sistema</Form.Label>
                            <Form.Select
                                value={formData.idUsuario}
                                onChange={handleUsuarioChange}
                            >
                                <option value="">— Sin usuario asignado —</option>
                                {usuarios.map(u => (
                                    <option key={u.id} value={u.id}>{u.nombre}</option>
                                ))}
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Al seleccionar un usuario se auto-completa el nombre de la referencia.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Nombre *</Form.Label>
                            <Form.Control
                                type="text"
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                                placeholder="Ej: Augusto García"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Descripción</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={formData.descripcion}
                                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                                placeholder="Descripción de la referencia"
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Color</Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                                <Form.Control
                                    type="color"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    style={{ width: '60px', height: '40px' }}
                                />
                                <Form.Control
                                    type="text"
                                    value={formData.color}
                                    onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                                    placeholder="#3B82F6"
                                />
                            </div>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancelar
                        </Button>
                        <Button variant="primary" type="submit" disabled={loading}>
                            {loading ? <Spinner animation="border" size="sm" /> : 'Guardar'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            <Modal show={showFusionModal} onHide={handleCerrarFusion} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Fusionar referencias</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small">
                        Todos los clientes de las referencias no elegidas quedarán reasignados a la referencia
                        que marques como canónica. Las demás se eliminan del catálogo.
                    </p>
                    {seleccionadasInfo.map((ref) => (
                        <Form.Check
                            key={ref.id}
                            type="radio"
                            id={`fusion-destino-${ref.id}`}
                            name="fusionDestino"
                            label={ref.nombre}
                            checked={String(ref.id) === idDestinoFusion}
                            onChange={() => setIdDestinoFusion(String(ref.id))}
                            className="mb-2"
                        />
                    ))}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCerrarFusion}>
                        Cancelar
                    </Button>
                    <Button variant="warning" onClick={handleConfirmarFusion} disabled={fusionando || !idDestinoFusion}>
                        {fusionando ? <Spinner animation="border" size="sm" /> : 'Fusionar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default GestionReferencias;
